import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Cpu } from "lucide-react";

// Privacy policy + legal mentions + AI Act transparency. `variant` selects
// which document to show ("privacy" | "legal").
export default function Legal({ variant = "privacy" }) {
  return (
    <div className="mx-auto max-w-3xl px-5 py-10" data-testid={`legal-${variant}`}>
      <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Retour au GPS
      </Link>
      {variant === "legal" ? <LegalMentions /> : <PrivacyPolicy />}
    </div>
  );
}

const H2 = ({ children }) => <h2 className="mt-6 mb-2 text-lg font-bold">{children}</h2>;
const P = ({ children }) => <p className="mb-3 text-sm leading-relaxed text-muted-foreground">{children}</p>;

function PrivacyPolicy() {
  return (
    <article>
      <div className="mb-4 flex items-center gap-2">
        <ShieldCheck className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-extrabold">Politique de confidentialité</h1>
      </div>
      <P>Dernière mise à jour : 2026. vspo respecte le Règlement Général sur la Protection des Données (RGPD).</P>

      <H2>Données que nous traitons</H2>
      <P>
        • <strong>Sans compte</strong> : vos préférences (thème, voix, réglages) et favoris/notes sont stockés
        localement dans votre navigateur. Votre position GPS est utilisée uniquement sur votre appareil pour la
        navigation et n'est pas conservée sur nos serveurs.
      </P>
      <P>
        • <strong>Avec compte (connexion Google)</strong> : nous conservons votre e-mail, nom et photo de profil,
        vos zones favorites et l'état de votre abonnement. Un cookie de session sécurisé vous identifie.
      </P>
      <P>
        • <strong>Paiement</strong> : les abonnements sont gérés par Stripe. Nous ne stockons jamais vos données
        de carte bancaire.
      </P>

      <H2>Sous-traitants et services tiers</H2>
      <P>
        Mapbox (cartes), TomTom (trafic), OpenRouteService (itinéraires), Stripe (paiement), Google (connexion),
        Open Charge Map & Opendatasoft/IRVE (bornes), données publiques OpenStreetMap (parkings). Chacun traite
        des données techniques strictement nécessaires au service.
      </P>

      <H2>Vos droits (RGPD)</H2>
      <P>
        Vous disposez d'un droit d'accès, de rectification, d'effacement et de portabilité. Depuis votre page
        <Link to="/compte" className="text-primary underline"> Mon compte</Link>, vous pouvez <strong>exporter vos données</strong>
        (format JSON) et <strong>supprimer votre compte</strong> à tout moment.
      </P>

      <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
        <div className="mb-1 flex items-center gap-2 font-semibold">
          <Cpu className="h-5 w-5 text-amber-500" /> Transparence IA (Règlement européen sur l'IA — AI Act)
        </div>
        <P>
          Certaines fonctions de vspo utilisent l'intelligence artificielle : la voix de guidage « Le Génie »
          et ses anecdotes, ainsi que des contenus éditoriaux, peuvent être générés automatiquement. Ces contenus
          sont fournis à titre informatif et peuvent contenir des imprécisions. Aucune décision automatisée
          ayant un effet juridique n'est prise à votre égard. Vous gardez toujours le contrôle de la conduite et
          pouvez désactiver la voix IA dans les réglages.
        </P>
      </div>

      <H2>Contact</H2>
      <P>Pour toute question relative à vos données : contact@vspo.app</P>
    </article>
  );
}

function LegalMentions() {
  return (
    <article>
      <h1 className="mb-4 text-2xl font-extrabold">Mentions légales</h1>
      <H2>Éditeur</H2>
      <P>vspo — application de navigation GPS pour voitures sans permis. Contact : contact@vspo.app</P>
      <H2>Hébergement</H2>
      <P>Application hébergée sur une infrastructure cloud sécurisée (Kubernetes).</P>
      <H2>Propriété intellectuelle</H2>
      <P>
        Les contenus de vspo sont protégés. Les données cartographiques proviennent de Mapbox et d'OpenStreetMap
        (© contributeurs OpenStreetMap). Les données de bornes proviennent d'IRVE/Opendatasoft et d'Open Charge Map.
      </P>
      <H2>Responsabilité</H2>
      <P>
        vspo fournit une aide à la navigation à titre indicatif. Le conducteur reste seul responsable du respect
        du code de la route et de la signalisation.
      </P>
      <P>
        Voir aussi notre <Link to="/confidentialite" className="text-primary underline">Politique de confidentialité</Link>.
      </P>
    </article>
  );
}
